/**
 * Facturación ARCA, egresos, proveedores y convenios — esquemas del contrato (AF §9.1..9.3).
 *
 * Lo que estos esquemas hacen cumplir antes de que el request toque el servicio:
 * - **RN-CON-02**: una Factura A exige CUIT del receptor; una B sobre el tope de
 *   consumidor final exige DNI. El esquema lo pide; el servicio lo vuelve a validar
 *   contra la ficha (la validación de forma no reemplaza la de regla).
 * - **RN-CON-07**: una nota de crédito lleva motivo obligatorio y referencia al original.
 * - **RN-CON-13**: un egreso se aprueba con comprobante, o con la marca explícita
 *   «sin comprobante» + motivo.
 * - **RN-CON-21**: lo que informa el convenio se CARGA, nunca pisa el registro propio.
 *
 * Fuente única: el backend los importa desde `modules/contable/schemas.ts`, no los
 * redefine.
 */
import { z } from "zod";
import { EXPENSE_METHODS, EXPENSE_STATUSES, INVOICE_PENDING_CODES, INVOICE_RECEIVER_DOC_TYPES, INVOICE_RECEIVER_KINDS, INVOICE_STATUSES, INVOICE_TYPES, PLATFORMS, PLATFORM_SETTLEMENT_STATUSES, SUPPLIER_PAYMENT_MODES, } from "../enums.js";
import { dateISO, exportFormat, listQuery, moneySchema, objectId, reasonRequired, yearMonth, } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Comprobantes (RN-CON-01..08)
// ─────────────────────────────────────────────────────────────────────────────
export const invoiceType = z.enum(INVOICE_TYPES);
export const invoiceStatus = z.enum(INVOICE_STATUSES);
export const invoiceReceiverKind = z.enum(INVOICE_RECEIVER_KINDS);
export const invoiceReceiverDocType = z.enum(INVOICE_RECEIVER_DOC_TYPES);
/**
 * **Factura pendiente** de una venta o cobro que SÍ se completó (RN-CON-06): la plata ya
 * entró y el comprobante sale después. Viaja en la respuesta de la venta del kiosko para
 * que la tablet/portal muestre «compra completada» y no un error de pago.
 */
export const invoicePending = z.object({
    code: z.enum(INVOICE_PENDING_CODES),
    reason: z.string(),
});
/**
 * **Letra** del comprobante, que es lo que el usuario elige. El `type` completo
 * (`FA`/`NCB`/`NDA`…) lo arma el backend cruzando letra + clase: pedirle a la SPA que
 * mande `NCA` sería pedirle que conozca la tabla de ARCA.
 *
 * `C` existe en el enum del emisor pero queda **inactivo** mientras la condición fiscal
 * sea RI: un Responsable Inscripto no emite C (RN-CON-02, PV-75).
 */
export const invoiceLetter = z.enum(["A", "B", "C"]);
/** CUIT argentino: 11 dígitos. El dígito verificador lo valida el backend. */
export const cuitSchema = z
    .string()
    .trim()
    .regex(/^\d{11}$/, "El CUIT son 11 dígitos, sin guiones");
export const dniSchema = z
    .string()
    .trim()
    .regex(/^\d{7,9}$/, "El DNI son 7 a 9 dígitos");
/**
 * Receptor del comprobante. Con letra `A` el CUIT es obligatorio (RN-CON-02); con `B`
 * sobre el tope de consumidor final, el DNI — el backend lo exige comparando contra
 * `arca.finalConsumerCap` y lo toma de la ficha del socio.
 */
export const invoiceReceiverInput = z.object({
    name: z.string().trim().max(200).optional(),
    docType: invoiceReceiverDocType.optional(),
    docNumber: z.string().trim().max(20).optional(),
    fiscalCondition: z.string().trim().max(80).optional(),
});
/**
 * **Facturar a demanda** un cobro (RN-CON-04): el botón «Facturar» del portal sobre un
 * cobro en efectivo, y el pedido del socio desde la app.
 */
export const issueInvoiceBody = z.object({
    paymentId: objectId.optional(),
    saleId: objectId.optional(),
    /** Si no viene, la decide RN-CON-02 (B por default, A a quien la pide con CUIT). */
    letter: invoiceLetter.optional(),
    receiver: invoiceReceiverInput.optional(),
});
/**
 * **Nota de crédito** (RN-CON-07): rol mínimo administración, motivo obligatorio y
 * referencia al comprobante original — que es el `:id` de la ruta.
 */
export const creditNoteBody = z.object({
    reason: reasonRequired,
    /** NC parcial: si no viene, se acredita el total del original. */
    amount: moneySchema.int().positive().optional(),
});
/** Nota de **débito** (RN-CON-02): mismo circuito, signo contrario. */
export const debitNoteBody = z.object({
    reason: reasonRequired,
    amount: moneySchema.int().positive(),
    description: z.string().trim().min(3).max(200),
});
export const listInvoicesQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    type: invoiceType.optional(),
    letter: invoiceLetter.optional(),
    status: invoiceStatus.optional(),
    pointOfSale: z.coerce.number().int().min(1).optional(),
    memberId: objectId.optional(),
    paymentId: objectId.optional(),
    /** Receptor por documento: es lo que busca administración cuando el socio reclama. */
    docNumber: z.string().trim().max(20).optional(),
});
/** Cobros sin comprobante: la lista del botón «Facturar» (RN-CON-04). */
export const uninvoicedPaymentsQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    memberId: objectId.optional(),
});
/** Reintento manual de la cola de CAE (RN-CON-06). */
export const retryCaeBody = z.object({
    /** Sin ids, reintenta toda la cola pendiente (con su límite). */
    invoiceIds: z.array(objectId).max(200).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Exportes contables (RN-CON-10)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Los cuatro exportes del AF §9.5 más el **formato importable de ARCA** del Libro IVA
 * Digital, que es un TXT de ancho fijo con dos archivos (cabecera y alícuotas).
 */
export const accountantExportKind = z.enum([
    "sales_vat_book",
    "expenses_book",
    "payments_by_method",
    "kiosk_sales",
]);
/** `arca_txt` sólo aplica al libro IVA Ventas: es su diseño de registro oficial. */
export const accountantExportFormat = z.enum(["csv", "xlsx", "arca_txt"]);
export const accountantExportQuery = z.object({
    kind: accountantExportKind,
    format: accountantExportFormat.default("csv"),
    /** Período: mes completo, o rango explícito. */
    month: yearMonth.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    branchId: objectId.optional(),
});
/** Reenvío manual del paquete mensual al contador (RN-CON-10, PV-70). */
export const sendAccountantPackageBody = z.object({
    month: yearMonth,
    /** Si no viene, la casilla de `settings.accountant.export`. */
    email: z.string().email().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Egresos (RN-CON-11..16)
// ─────────────────────────────────────────────────────────────────────────────
export const expenseMethod = z.enum(EXPENSE_METHODS);
export const expenseStatus = z.enum(EXPENSE_STATUSES);
/** Línea de compra de mercadería: al aprobar suma stock y actualiza costo (RN-CON-16). */
export const stockPurchaseLine = z.object({
    productId: objectId,
    qty: z.number().int().positive(),
    unitCost: moneySchema.int().min(0),
    /** Sólo en productos que controlan vencimiento (RN-CON-16/28). */
    lotExpiry: dateISO.optional(),
});
/**
 * Alta de egreso (RN-CON-11). Nace **pendiente** salvo que caiga bajo el umbral de
 * auto-aprobación (`expense.autoApproveThreshold`, default $0 → todo se aprueba a mano).
 * `branchId` puede ser la sede virtual **Central**, que prorratea (RN-CON-17).
 */
export const createExpenseBody = z.object({
    branchId: objectId,
    categoryCode: z.string().trim().min(2).max(60),
    /** RN-CON-18bis: egreso directo de una actividad, para la rentabilidad por disciplina. */
    disciplineId: objectId.optional(),
    supplierId: objectId.optional(),
    amount: moneySchema.int().positive(),
    method: expenseMethod,
    date: dateISO,
    receiptKey: z.string().trim().min(1).max(300).optional(),
    /** RN-CON-13: la única excepción al comprobante, explícita y con motivo. */
    noReceiptReason: reasonRequired.optional(),
    stockPurchase: z.array(stockPurchaseLine).max(100).optional(),
    notes: z.string().trim().max(1000).optional(),
});
/** Edición mientras está `pending` (caso borde 9: el egreso cargado en la sede equivocada). */
export const updateExpenseBody = createExpenseBody.partial();
/** Aprobación / rechazo: dueños o administración, con motivo auditado (RN-CON-12, D5). */
export const approveExpenseBody = z.object({
    reason: z.string().trim().max(500).optional(),
});
export const rejectExpenseBody = z.object({ reason: reasonRequired });
/** Anulación de un egreso YA aprobado: sólo con motivo auditado (caso borde 9). */
export const voidExpenseBody = z.object({ reason: reasonRequired });
export const listExpensesQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    status: expenseStatus.optional(),
    categoryCode: z.string().trim().max(60).optional(),
    supplierId: objectId.optional(),
    disciplineId: objectId.optional(),
    method: expenseMethod.optional(),
    /** RN-CON-13: filtro de los que se aprobaron SIN comprobante. */
    noReceipt: z.coerce.boolean().optional(),
    format: exportFormat.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Caja chica (RN-CON-14)
// ─────────────────────────────────────────────────────────────────────────────
/** El fondo **nace desactivado**: `active` no se puede setear en el alta. */
export const createPettyCashFundBody = z.object({
    branchId: objectId,
    responsibleUserId: objectId,
    /** Si no viene, el default de `settings.pettyCash.capDefault` (PV-72). */
    cap: moneySchema.int().min(0).optional(),
});
export const updatePettyCashFundBody = z.object({
    responsibleUserId: objectId.optional(),
    cap: moneySchema.int().min(0).optional(),
});
/** Activar / desactivar la caja chica queda auditado (RN-CON-14). */
export const togglePettyCashBody = z.object({
    active: z.boolean(),
    reason: reasonRequired,
});
/** Descarga de un gasto menor contra el fondo. Crea el egreso y lo descuenta. */
export const pettyCashExpenseBody = z.object({
    categoryCode: z.string().trim().min(2).max(60),
    supplierId: objectId.optional(),
    amount: moneySchema.int().positive(),
    date: dateISO.optional(),
    receiptKey: z.string().trim().min(1).max(300).optional(),
    noReceiptReason: reasonRequired.optional(),
    notes: z.string().trim().max(1000).optional(),
});
/** Reposición: es un egreso normal que pasa por la aprobación de RN-CON-12. */
export const pettyCashReplenishBody = z.object({
    amount: moneySchema.int().positive(),
    method: expenseMethod.default("transfer"),
    receiptKey: z.string().trim().min(1).max(300).optional(),
    noReceiptReason: reasonRequired.optional(),
    notes: z.string().trim().max(1000).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Proveedores (RN-CON-15)
// ─────────────────────────────────────────────────────────────────────────────
export const supplierPaymentMode = z.enum(SUPPLIER_PAYMENT_MODES);
export const createSupplierBody = z.object({
    businessName: z.string().trim().min(2).max(200),
    cuit: cuitSchema.optional(),
    ivaCondition: z.string().trim().max(80).optional(),
    category: z.string().trim().min(2).max(80),
    contact: z
        .object({
        name: z.string().trim().max(120).optional(),
        phone: z.string().trim().max(40).optional(),
        email: z.string().email().optional(),
    })
        .optional(),
    paymentMode: supplierPaymentMode.default("on_stock"),
});
export const updateSupplierBody = createSupplierBody.partial().extend({
    active: z.boolean().optional(),
});
export const listSuppliersQuery = listQuery.extend({
    category: z.string().trim().max(80).optional(),
    paymentMode: supplierPaymentMode.optional(),
    active: z.coerce.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Convenios y liquidaciones (RN-CON-19..22)
// ─────────────────────────────────────────────────────────────────────────────
export const platform = z.enum(PLATFORMS);
export const platformSettlementStatus = z.enum(PLATFORM_SETTLEMENT_STATUSES);
/** RN-CON-19: el valor unitario es VERSIONADO (PV-35). Nunca se edita el vigente. */
export const platformUnitValueBody = z.object({
    amount: moneySchema.int().min(0),
    effectiveFrom: dateISO,
});
export const upsertPlatformAgreementBody = z.object({
    platform,
    billing: z
        .object({
        contactEmail: z.string().email().or(z.literal("")).optional(),
        businessName: z.string().trim().max(200).optional(),
        cuit: cuitSchema.optional(),
        fiscalCondition: z.string().trim().max(80).optional(),
    })
        .optional(),
    maxVisitorsPerClassDefault: z.number().int().min(0).nullable().optional(),
    active: z.boolean().optional(),
});
/** Pre-liquidación de un mes a mano (el job la arma sola — RN-CON-20). */
export const generateSettlementBody = z.object({
    platformAgreementId: objectId,
    month: yearMonth,
});
/**
 * RN-CON-21: administración carga **lo que el convenio informó**. El registro propio no
 * se toca: la diferencia se muestra y el detalle de `accessLogs` es el respaldo.
 */
export const reportSettlementBody = z.object({
    count: z.number().int().min(0),
    amount: moneySchema.int().min(0),
    at: dateISO.optional(),
});
/** Cerrada = inmutable; cualquier corrección posterior es un ajuste auditado (RN-CON-20). */
export const settlementAdjustmentBody = z.object({
    reason: reasonRequired,
    amount: moneySchema.int(),
});
export const settlementStatusBody = z.object({
    status: platformSettlementStatus,
});
export const listSettlementsQuery = listQuery.extend({
    platformAgreementId: objectId.optional(),
    platform: platform.optional(),
    month: yearMonth.optional(),
    status: platformSettlementStatus.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// App de socios — «Mis comprobantes» (AF §9.5)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * El socio pide la factura de un pago no facturado. Si pide **A**, carga su CUIT y su
 * condición fiscal (RN-CON-02); si pide B, se emite con los datos de la ficha.
 */
export const requestMyInvoiceBody = z
    .object({
    paymentId: objectId,
    letter: z.enum(["A", "B"]).default("B"),
    cuit: cuitSchema.optional(),
    taxCondition: z.string().trim().max(80).optional(),
})
    .refine((v) => v.letter !== "A" || Boolean(v.cuit), {
    message: "La Factura A necesita tu CUIT (RN-CON-02)",
    path: ["cuit"],
});
//# sourceMappingURL=contable.js.map